import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield, Loader2, Check, AlertCircle, User, Users, Trash2, Plus, X } from 'lucide-react';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth.store';
import { clsx } from 'clsx';
import type { AuthUser, TeamMember } from '@pulse/shared';

export function Settings() {
  const { user, checkAuth, updateUser } = useAuthStore();
  const [totpSetup, setTotpSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
  });

  // Team member form
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberForm, setMemberForm] = useState({ displayName: '', email: '', password: '' });
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Team query
  const teamQuery = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<{ members: TeamMember[] }>('/auth/team'),
  });

  // Profile mutation
  const profileMutation = useMutation({
    mutationFn: (data: { displayName?: string; email?: string; currentPassword: string; newPassword?: string }) =>
      api.put<{ user: AuthUser }>('/auth/profile', data),
    onSuccess: (data) => {
      updateUser(data.user);
      setProfileForm((prev) => ({ ...prev, currentPassword: '', newPassword: '' }));
      setMessage({ type: 'success', text: 'Profile updated' });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update profile' });
    },
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: (data: { email: string; password: string; displayName: string }) =>
      api.post<{ user: AuthUser }>('/auth/team', data),
    onSuccess: () => {
      setMemberForm({ displayName: '', email: '', password: '' });
      setShowAddMember(false);
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setMessage({ type: 'success', text: 'Team member added' });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to add member' });
    },
  });

  // Delete member mutation
  const deleteMemberMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/team/${id}`),
    onSuccess: () => {
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setMessage({ type: 'success', text: 'Team member removed' });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to remove member' });
    },
  });

  const setupMutation = useMutation({
    mutationFn: () => api.post<{ secret: string; otpauthUrl: string }>('/auth/2fa/setup', {}),
    onSuccess: (data) => {
      setTotpSetup(data);
      setMessage(null);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to setup 2FA' });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (code: string) => api.post<{ backupCodes: string[] }>('/auth/2fa/verify', { code }),
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setTotpSetup(null);
      setVerifyCode('');
      checkAuth();
      setMessage({ type: 'success', text: '2FA enabled successfully' });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Invalid code' });
    },
  });

  const disableMutation = useMutation({
    mutationFn: (code: string) => api.post('/auth/2fa/disable', { code }),
    onSuccess: () => {
      setDisableCode('');
      checkAuth();
      setMessage({ type: 'success', text: '2FA disabled' });
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Invalid code' });
    },
  });

  function handleProfileSubmit(e: React.FormEvent) {
    e.preventDefault();
    const data: { displayName?: string; email?: string; currentPassword: string; newPassword?: string } = {
      currentPassword: profileForm.currentPassword,
    };
    if (profileForm.displayName !== (user?.displayName || '')) data.displayName = profileForm.displayName;
    if (profileForm.email !== user?.email) data.email = profileForm.email;
    if (profileForm.newPassword) data.newPassword = profileForm.newPassword;
    profileMutation.mutate(data);
  }

  function handleAddMember(e: React.FormEvent) {
    e.preventDefault();
    addMemberMutation.mutate(memberForm);
  }

  const members = teamQuery.data?.members || [];

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-xl font-semibold text-gray-100 mb-6">Settings</h1>

      {message && (
        <div
          className={clsx(
            'flex items-center gap-2 p-4 mb-6 rounded-lg',
            message.type === 'success'
              ? 'bg-green-500/10 border border-green-500/20 text-green-400'
              : 'bg-red-500/10 border border-red-500/20 text-red-400'
          )}
        >
          {message.type === 'success' ? (
            <Check className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Profile */}
      <div className="card">
        <div className="card-header flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-medium text-gray-300">Profile</h2>
        </div>
        <div className="card-body">
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <label htmlFor="displayName" className="label">Display Name</label>
              <input
                id="displayName"
                type="text"
                value={profileForm.displayName}
                onChange={(e) => setProfileForm((p) => ({ ...p, displayName: e.target.value }))}
                className="input"
                placeholder="Your name"
              />
            </div>
            <div>
              <label htmlFor="email" className="label">Email</label>
              <input
                id="email"
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm((p) => ({ ...p, email: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label htmlFor="newPassword" className="label">
                New Password <span className="text-gray-500 font-normal">(leave blank to keep current)</span>
              </label>
              <input
                id="newPassword"
                type="password"
                value={profileForm.newPassword}
                onChange={(e) => setProfileForm((p) => ({ ...p, newPassword: e.target.value }))}
                className="input"
                placeholder="Min 12 characters"
              />
            </div>
            <hr className="border-gray-700" />
            <div>
              <label htmlFor="currentPassword" className="label">Current Password <span className="text-red-400">*</span></label>
              <input
                id="currentPassword"
                type="password"
                value={profileForm.currentPassword}
                onChange={(e) => setProfileForm((p) => ({ ...p, currentPassword: e.target.value }))}
                className="input"
                placeholder="Required to save changes"
              />
            </div>
            <button
              type="submit"
              disabled={!profileForm.currentPassword || profileMutation.isPending}
              className="btn-primary w-full"
            >
              {profileMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
            </button>
          </form>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="card mt-6">
        <div className="card-header flex items-center gap-2">
          <Shield className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-medium text-gray-300">Two-Factor Authentication</h2>
        </div>
        <div className="card-body">
          {backupCodes ? (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-400 font-medium mb-2">
                  Save these backup codes
                </p>
                <p className="text-xs text-gray-400 mb-3">
                  Store these codes in a safe place. You can use them to access your account if you lose your authenticator.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, i) => (
                    <code key={i} className="px-3 py-2 bg-gray-800 rounded text-sm font-mono text-center">
                      {code}
                    </code>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setBackupCodes(null)}
                className="btn-primary w-full"
              >
                I've saved these codes
              </button>
            </div>
          ) : totpSetup ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                Scan this QR code with your authenticator app:
              </p>
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpSetup.otpauthUrl)}`}
                  alt="2FA QR Code"
                  className="w-48 h-48"
                />
              </div>
              <p className="text-xs text-gray-500 text-center">
                Or enter this code manually: <code className="text-gray-300">{totpSetup.secret}</code>
              </p>
              <div>
                <label htmlFor="verifyCode" className="label">
                  Verification Code
                </label>
                <input
                  id="verifyCode"
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input text-center text-xl tracking-widest font-mono"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setTotpSetup(null);
                    setVerifyCode('');
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={() => verifyMutation.mutate(verifyCode)}
                  disabled={verifyCode.length !== 6 || verifyMutation.isPending}
                  className="btn-primary flex-1"
                >
                  {verifyMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Verify & Enable'
                  )}
                </button>
              </div>
            </div>
          ) : user?.totpEnabled ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-400">
                <Check className="h-4 w-4" />
                <span className="text-sm">Two-factor authentication is enabled</span>
              </div>
              <div>
                <label htmlFor="disableCode" className="label">
                  Enter code to disable 2FA
                </label>
                <input
                  id="disableCode"
                  type="text"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input text-center text-xl tracking-widest font-mono"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
              <button
                onClick={() => disableMutation.mutate(disableCode)}
                disabled={disableCode.length !== 6 || disableMutation.isPending}
                className="btn-danger w-full"
              >
                {disableMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Disable 2FA'
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                Add an extra layer of security to your account by enabling two-factor authentication.
              </p>
              <button
                onClick={() => setupMutation.mutate()}
                disabled={setupMutation.isPending}
                className="btn-primary w-full"
              >
                {setupMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Enable 2FA'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Team Members */}
      <div className="card mt-6">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-medium text-gray-300">Team</h2>
          </div>
          {!showAddMember && (
            <button
              onClick={() => setShowAddMember(true)}
              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
            >
              <Plus className="h-3 w-3" />
              Add Member
            </button>
          )}
        </div>
        <div className="card-body space-y-3">
          {showAddMember && (
            <form onSubmit={handleAddMember} className="p-4 bg-gray-800/50 rounded-lg space-y-3 mb-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-300">New Member</span>
                <button type="button" onClick={() => setShowAddMember(false)} className="text-gray-500 hover:text-gray-300">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div>
                <label htmlFor="memberName" className="label">Display Name</label>
                <input
                  id="memberName"
                  type="text"
                  value={memberForm.displayName}
                  onChange={(e) => setMemberForm((p) => ({ ...p, displayName: e.target.value }))}
                  className="input"
                  required
                />
              </div>
              <div>
                <label htmlFor="memberEmail" className="label">Email</label>
                <input
                  id="memberEmail"
                  type="email"
                  value={memberForm.email}
                  onChange={(e) => setMemberForm((p) => ({ ...p, email: e.target.value }))}
                  className="input"
                  required
                />
              </div>
              <div>
                <label htmlFor="memberPassword" className="label">Temporary Password</label>
                <input
                  id="memberPassword"
                  type="password"
                  value={memberForm.password}
                  onChange={(e) => setMemberForm((p) => ({ ...p, password: e.target.value }))}
                  className="input"
                  placeholder="Min 12 characters"
                  required
                  minLength={12}
                />
              </div>
              <button
                type="submit"
                disabled={addMemberMutation.isPending}
                className="btn-primary w-full"
              >
                {addMemberMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Member'}
              </button>
            </form>
          )}

          {teamQuery.isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No team members yet</p>
          ) : (
            members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-100 truncate">
                      {member.displayName || member.email}
                    </span>
                    {member.id === user?.id && (
                      <span className="text-xs text-gray-500">(you)</span>
                    )}
                    {member.totpEnabled && (
                      <span className="text-xs bg-green-500/10 text-green-400 px-1.5 py-0.5 rounded">2FA</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{member.email}</p>
                </div>
                {member.id !== user?.id && (
                  deleteConfirm === member.id ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => deleteMemberMutation.mutate(member.id)}
                        disabled={deleteMemberMutation.isPending}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        {deleteMemberMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm'}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="text-xs text-gray-500 hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(member.id)}
                      className="text-gray-500 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
